import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { DEFAULT_DISPLAY_TIMEZONE, DISPLAY_TIMEZONE_STORAGE_KEY } from "../../core/constants";

const TimezoneContext = createContext(null);

function isValidTimezone(timeZone) {
  if (!timeZone) return false;
  try {
    Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
    return true;
  } catch (_) {
    return false;
  }
}

function resolveInitialTimezone() {
  const readCandidates = [];
  try {
    readCandidates.push(localStorage.getItem(DISPLAY_TIMEZONE_STORAGE_KEY));
  } catch (_) {
    // ignore storage failures
  }
  try {
    readCandidates.push(sessionStorage.getItem(DISPLAY_TIMEZONE_STORAGE_KEY));
  } catch (_) {
    // ignore storage failures
  }
  const stored = readCandidates.find((value) => isValidTimezone(value));
  if (stored) return stored;
  return DEFAULT_DISPLAY_TIMEZONE;
}

function toLocaleTag(locale) {
  return locale === "vi" ? "vi-VN" : "en-US";
}

function getOffsetMinutes(timeZone, date = new Date()) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = formatter.formatToParts(date);
  const map = {};
  parts.forEach((part) => {
    if (part.type !== "literal") map[part.type] = part.value;
  });
  const asUtcMs = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second),
  );
  return Math.round((asUtcMs - date.getTime()) / 60000);
}

function buildGmtLabel(timeZone, locale = "en", date = new Date()) {
  const offsetMinutes = getOffsetMinutes(timeZone, date);
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  if (minutes === 0) {
    return locale === "vi" ? `GMT ${sign}${hours}` : `GMT${sign}${hours}`;
  }
  return locale === "vi"
    ? `GMT ${sign}${hours}:${String(minutes).padStart(2, "0")}`
    : `GMT${sign}${hours}:${String(minutes).padStart(2, "0")}`;
}

function formatInTimezone(value, { locale = "en", timeZone, dateStyle = "medium", timeStyle = undefined } = {}) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(toLocaleTag(locale), {
    timeZone,
    dateStyle,
    timeStyle,
  }).format(date);
}

function getSupportedTimezones() {
  if (typeof Intl.supportedValuesOf === "function") {
    try {
      const zones = Intl.supportedValuesOf("timeZone");
      if (Array.isArray(zones) && zones.length > 0) return zones;
    } catch (_) {
      // fallback below
    }
  }
  return [DEFAULT_DISPLAY_TIMEZONE, "UTC"];
}

export function TimezoneProvider({ children }) {
  const [timezone, setTimezoneState] = useState(resolveInitialTimezone);

  const setTimezone = useCallback((nextTimezone) => {
    const safe = isValidTimezone(nextTimezone) ? nextTimezone : DEFAULT_DISPLAY_TIMEZONE;
    setTimezoneState(safe);
    try {
      localStorage.setItem(DISPLAY_TIMEZONE_STORAGE_KEY, safe);
    } catch (_) {
      // ignore storage failures
    }
    try {
      sessionStorage.setItem(DISPLAY_TIMEZONE_STORAGE_KEY, safe);
    } catch (_) {
      // ignore storage failures
    }
  }, []);

  const value = useMemo(() => ({
    timezone,
    setTimezone,
    supportedTimezones: getSupportedTimezones(),
    getGmtLabel: (locale = "en", date = new Date()) => buildGmtLabel(timezone, locale, date),
    getGmtLabelForTimezone: (timeZone, locale = "en", date = new Date()) =>
      buildGmtLabel(
        isValidTimezone(timeZone) ? timeZone : timezone,
        locale,
        date,
      ),
    formatInTimezone: (valueToFormat, options = {}) =>
      formatInTimezone(valueToFormat, { ...options, timeZone: timezone }),
  }), [timezone, setTimezone]);

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
}

export function useDisplayTimezone() {
  const ctx = useContext(TimezoneContext);
  if (!ctx) throw new Error("useDisplayTimezone must be used within TimezoneProvider");
  return ctx;
}
